import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Icon,
  Link,
  Tag,
  Text,
  Title,
} from '@nimbus-ds/components';
import { CheckIcon, WhatsappIcon } from '@nimbus-ds/icons';
import {
  useGetWhatsappCreditsBalanceQuery,
  useGetWhatsappCreditsPacksQuery,
  useGetWhatsappPurchasesQuery,
  useGetWhatsappNotificationSettingsQuery,
} from 'domains/Configurations/Notifications/notificationsApi';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import { WhatsAppBannerHistorySidebar } from './WhatsAppBannerHistorySidebar';
import { WhatsAppBannerPurchaseSidebar } from './WhatsAppBannerPurchaseSidebar';

interface WhatsAppBannerProps {
  limitedActivityNotice?: boolean;
}

export function WhatsAppBanner({
  limitedActivityNotice = false,
}: Readonly<WhatsAppBannerProps>) {
  const t = useTranslationConfigurations('notifications.whatsapp');
  const [open, setOpen] = useState<string | null>(null);

  const { data: settings, isLoading: isLoadingSettings } =
    useGetWhatsappNotificationSettingsQuery();
  const { data: creditsPacks, isLoading: isLoadingCreditsPacks } =
    useGetWhatsappCreditsPacksQuery();
  const { data: creditsBalanceData, isLoading: isLoadingCreditsBalance } =
    useGetWhatsappCreditsBalanceQuery();
  const { data: purchasesData, isLoading: isLoadingPurchases } =
    useGetWhatsappPurchasesQuery();

  const isConnected = settings?.whatsapp.is_connected;
  const isHistoryOpen = open === 'history';
  const isPurchaseOpen = open === 'purchase';

  const creditsBalance = creditsBalanceData?.balance ?? 0;

  if (isLoadingSettings || isLoadingCreditsBalance || isLoadingPurchases) {
    return null;
  }

  if (limitedActivityNotice && creditsBalance > 0) {
    return null;
  }

  return (
    <>
      <Card>
        <Card.Header>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-start"
              gap="2"
            >
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                gap="2"
              >
                <Icon
                  color="primary-textLow"
                  source={<WhatsappIcon size="medium" />}
                />
                <Title as="h4">{t('banner.title')}</Title>
              </Box>

              {isConnected ? (
                <Tag appearance="success">
                  <CheckIcon color="success-textLow" size={12} />
                  <Text color="success-textLow">
                    {settings?.whatsapp.telephone}
                  </Text>
                </Tag>
              ) : (
                <Tag appearance="neutral">
                  <Text color="neutral-textLow">
                    {t('banner.notConnected')}
                  </Text>
                </Tag>
              )}
            </Box>

            {isConnected ? (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                gap="4"
              >
                <Link
                  appearance="primary"
                  textDecoration="none"
                  as="button"
                  onClick={() => setOpen('history')}
                  disabled={isLoadingPurchases}
                >
                  {t('banner.viewHistory')}
                </Link>

                <Button
                  appearance="primary"
                  onClick={() => setOpen('purchase')}
                  disabled={isLoadingCreditsPacks}
                >
                  {t('banner.buyCredits')}
                </Button>
              </Box>
            ) : null}
          </Box>
        </Card.Header>

        <Card.Body>
          {isConnected ? (
            creditsBalance !== 0 ? (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                gap="2"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  flexDirection="row"
                  gap="0-5"
                  marginRight="1"
                >
                  <Text fontSize="base" fontWeight="regular">
                    {t('banner.availableMessagesLabel')}
                  </Text>
                  <Text
                    fontSize="base"
                    fontWeight="bold"
                    color="neutral-textHigh"
                  >
                    {creditsBalance}
                  </Text>
                </Box>
              </Box>
            ) : (
              <Text>{t('banner.noCreditsAvailable')}</Text>
            )
          ) : (
            <Text>{t('banner.disconnectedDescription')}</Text>
          )}
        </Card.Body>

        {isConnected && creditsBalance === 0 ? (
          <Alert appearance="warning" title="">
            {t('banner.noCreditsAlert')}
          </Alert>
        ) : null}
      </Card>

      <WhatsAppBannerHistorySidebar
        isOpen={isHistoryOpen}
        onClick={() => setOpen(null)}
        onRemove={() => setOpen(null)}
        purchasesData={purchasesData}
      />

      <WhatsAppBannerPurchaseSidebar
        isOpen={isPurchaseOpen}
        onClick={() => setOpen(null)}
        onRemove={() => setOpen(null)}
        creditsPacks={creditsPacks}
      />
    </>
  );
}
